import {
  isScheduleNode,
  isTeamNode,
} from '@sportninja/common/features/competition/operations/utils/nodeTypes'
import type { OperationsNode } from '@sportninja/common/features/competition/operations/types/operations.types'

export interface AddableChildren {
  division: boolean
  team: boolean
}

/**
 * Whether a node may be deleted, mirroring the legacy tree's own guard
 * (`ScheduleTree/index.jsx:895-898`, which hid the button on
 * `subtitle !== 'Season' && !(isTeamNode && is_registration_on)`).
 *
 * Two rules, for different reasons:
 *
 * - The **root season** is the competition itself. Deleting it from inside its
 *   own Setup tab is not what the row menu is for — the competition is removed
 *   from its own page instead.
 * - A **team** is undeletable while registration is open. Registered teams
 *   belong to the registration flow; removing one behind its back strands the
 *   registration that created it. This is the mirror of `canAddChild`'s
 *   add-team suppression — the same reasoning in the opposite direction.
 *
 * Divisions and conferences stay deletable throughout: the structure is still
 * the organizer's to change while registration runs.
 */
export const canDeleteNode = (
  node: OperationsNode,
  isRegistrationOn = false
): boolean => {
  if (isTeamNode(node)) {
    return !isRegistrationOn
  }
  return node.type !== 'season'
}

/**
 * Which children a node may take, mirroring the legacy tree's button rules
 * (`ScheduleTree/index.jsx:863-892`).
 *
 * - A **team** holds nothing.
 * - A **division** is the bottom of the competition hierarchy: it holds teams,
 *   never another competition. The API permits arbitrary nesting, but offering
 *   "Add Division" while a division is selected reads as "add a sibling" and
 *   would instead bury one inside — so the legacy tree gated it on
 *   `!isDivision`, and so do we.
 * - A **conference** is a pure container: it holds divisions, never teams
 *   directly (legacy gated add-team on `!isConference`).
 * - A **season** may hold either, but not both at once — teams and
 *   sub-competitions never coexist under one parent, so once it holds child
 *   schedules it can no longer take teams directly.
 *
 * `isRegistrationOn` suppresses ADD TEAM everywhere while registration is
 * open: teams then arrive through the Registration tab, and adding one by hand
 * behind an open registration produces a team the registration flow does not
 * know about. Divisions are unaffected — the structure still has to be built.
 * Passing it here rather than gating at each call site keeps the tree row and
 * the panel header from drifting apart.
 */
export const canAddChild = (
  node: OperationsNode,
  isRegistrationOn = false
): AddableChildren => {
  if (isTeamNode(node)) {
    return { division: false, team: false }
  }

  if (isRegistrationOn) {
    return { division: node.type !== 'division', team: false }
  }

  const holdsChildSchedules = node.children.some(isScheduleNode)

  if (node.type === 'division') {
    return { division: false, team: true }
  }

  if (node.type === 'conference') {
    return { division: true, team: false }
  }

  return { division: true, team: !holdsChildSchedules }
}
